import { type ErrorReport, reportError } from "@/lib/error-tracking";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "./ui/button";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Error boundary da aplicação — captura erros de renderização de qualquer
 * componente filho, reporta ao backend e exibe um fallback em vez de tela
 * branca.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // exactOptionalPropertyTypes: só inclui os campos opcionais quando definidos.
    const report: ErrorReport = { kind: "react", message: error.message };
    if (error.stack) {
      report.stack = error.stack;
    }
    if (info.componentStack) {
      report.componentStack = info.componentStack;
    }
    reportError(report);
  }

  private readonly handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div
          role="alert"
          className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 py-12 text-center"
        >
          <h1 className="text-2xl font-semibold text-foreground">Algo deu errado</h1>
          <p className="max-w-md text-sm text-muted-foreground">
            Ocorreu um erro inesperado ao exibir esta página. A equipe foi notificada.
          </p>
          <Button variant="outline" onClick={this.handleReload}>
            Recarregar página
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}
